/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import ModalInput from "../../ui/ModalTextInput";
import Dropdown from "../../ui/SelectDropdown";
import Button from "../../ui/Button";
import { RootState } from "../../../redux/app/store";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  fetchCategoriesAsync,
  fetchSizeChartsAsync,
  updateProductDetailsAsync,
} from "../../../redux/features/ProductSlice";
import MultiSelectDropdown from "../../ui/MultiSelectDropdown";

export interface SizeItem {
  label: string;
  quantity: number;
}

export interface EditProductFormData {
  name: string;
  description: string;
  price: string;
  quantity: number;
  colors: string[];
  sizeChartId: string;
  tags: string[];
  categoryIds: string; // single string now
}

interface EditProductModalProps {
  open: boolean;
  onClose: () => void;
  product: any;
  sizeCharts?: Array<{ label: string; value: string }>;
  onSave: () => void;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  open,
  onClose,
  product,
  onSave,
}) => {
  const { categories, sizeCharts, loading } = useAppSelector(
    (state: RootState) => state.products,
  );
  const [formData, setFormData] = useState<EditProductFormData>({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || "",
    quantity: product?.quantity || 0,
    colors: product?.colors || [],
    sizeChartId: product?.sizeChart?.id || "",
    tags: product?.tags || [],
    categoryIds: product?.categoryIds?.id || "",
  });

  // Editable sizes state
  const [editableSizes, setEditableSizes] = useState(
    product?.sizes?.map((s: any) => ({
      id: s.id,
      sizeVariantId: s.sizeVariantId || s.sizeVariant?.id,
      label: s.label || s.sizeVariant?.label,
      stock: s.stock ?? 0,
      sizePrice: s.sizePrice ?? 0,
    })) || [],
  );

  // If you still need selectedVariants for other logic, keep it, otherwise remove it if unused
  const [selectedVariants, setSelectedVariants] = useState(
    product?.sizes?.map((s: any) => ({
      label: s.sizeVariant?.label || s.label,
      value: s.sizeVariant?.id || s.sizeVariantId,
      quantity: s.stock ?? 0,
      stockPrice: s.sizePrice,
    })) || [],
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  // Get variants of selected size chart
  const variantsForSelectedSizeChart =
    sizeCharts?.items
      ?.find((sc: any) => sc.id === formData.sizeChartId)
      ?.variants?.map((v: any) => ({
        label: v.label,
        value: v.id,
      })) || [];

  // Combine all subcategories into one list
  const allSubcategories =
    categories
      ?.flatMap((cat: any) => cat.subcategories || [])
      ?.map((sub: any) => ({
        label: sub.title,
        value: sub.id,
      })) || [];
  // Combine all subcategories into one list
  const sizeChartByCategory =
    sizeCharts?.items?.map((sub: any) => ({
      label: sub.name,
      value: sub.id,
    })) || [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = name === "quantity" ? Number(value) : value;

    setFormData({
      ...formData,
      [name]: parsedValue,
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    const updatedData = {
      ...formData,
      sizes: editableSizes?.map((s: any) => ({
        id: s.id,
        sizeVariantId: s.sizeVariantId,
        label: s.label,
        stock: s.stock,
        sizePrice: s.sizePrice,
      })),
    };

    try {
      await dispatch(
        updateProductDetailsAsync({ id: product?.id, updatedData }),
      ).unwrap();

      onSave?.();
      onClose?.();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  useEffect(() => {
    if (!categories?.length) {
      dispatch(fetchCategoriesAsync());
    }
  }, []);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Product"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            label="Cancel"
            onClick={onClose}
            layout="inline"
            variant="ghost"
            minWidthClass="min-w-[120px]"
          />
          <Button
            label="Save"
            onClick={handleSave}
            layout="inline"
            minWidthClass="min-w-[120px]"
            isLoading={loading}
          />
        </div>
      }
    >
      <div className="p-6">
        <ModalInput
          label="Product Name"
          name="name"
          value={formData?.name ?? ""}
          onChange={handleInputChange}
          required
          error={errors?.name}
        />

        <ModalInput
          label="Description"
          name="description"
          value={formData?.description ?? ""}
          onChange={handleInputChange}
          required
          error={errors?.description}
        />

        <Dropdown
          label="Category"
          name="categoryIds"
          options={allSubcategories ?? []}
          value={formData?.categoryIds ?? ""}
          onSelect={(value) => setFormData({ ...formData, categoryIds: value })}
          placeholder="Select a subcategory"
        />

        {/* Editable Sizes */}
        {editableSizes?.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Sizes</label>
            {editableSizes.map((size: any, index: any) => (
              <div key={size?.id} className="flex items-center gap-4 mb-3">
                {/* Editable Label */}
                <input
                  type="text"
                  value={size?.label ?? ""}
                  onChange={(e) => {
                    const updated = [...editableSizes];
                    updated[index].label = e.target.value;
                    setEditableSizes(updated);
                  }}
                  className="w-28 border border-gray-300 rounded px-2 py-1"
                  placeholder="Size Label (e.g. XL)"
                />

                {/* Stock Input */}
                <input
                  type="number"
                  min="0"
                  value={size?.stock ?? 0}
                  onChange={(e) => {
                    const updated = [...editableSizes];
                    updated[index].stock = parseInt(e.target.value) || 0;
                    setEditableSizes(updated);
                  }}
                  className="w-24 border border-gray-300 rounded px-2 py-1"
                  placeholder="Stock"
                />

                {/* Price Input */}
                <input
                  type="number"
                  min="0"
                  value={size?.sizePrice ?? 0}
                  onChange={(e) => {
                    const updated = [...editableSizes];
                    updated[index].sizePrice = parseInt(e.target.value) || 0;
                    setEditableSizes(updated);
                  }}
                  className="w-28 border border-gray-300 rounded px-2 py-1"
                  placeholder="Price"
                />
              </div>
            ))}
          </div>
        )}

        {/* Variants dropdown (only if product.sizeChart.id is missing) */}
        {!product?.sizeChart?.id &&
          variantsForSelectedSizeChart?.length > 0 && (
            <div className="mt-4">
              <Dropdown
                label="Variants"
                name="variants"
                options={variantsForSelectedSizeChart}
                value=""
                onSelect={(value) => {
                  const variant = variantsForSelectedSizeChart.find(
                    (v: any) => v.value === value,
                  );
                  if (variant) {
                    setSelectedVariants([
                      ...selectedVariants,
                      {
                        id: variant.value,
                        label: variant.label,
                        stock: 0,
                        sizePrice: 0,
                      },
                    ]);
                  }
                }}
                placeholder="Select a variant"
              />

              {/* Editable selected variants */}
              {selectedVariants.map((v: any, idx: number) => (
                <div key={v.id} className="flex items-center gap-4 mt-2 mb-2">
                  <input
                    type="text"
                    value={v.label ?? ""}
                    onChange={(e) => {
                      const updated = [...selectedVariants];
                      updated[idx].label = e.target.value;
                      setSelectedVariants(updated);
                    }}
                    className="w-28 border border-gray-300 rounded px-2 py-1"
                    placeholder="Label"
                  />

                  <input
                    type="number"
                    min="0"
                    value={v.stock ?? 0}
                    onChange={(e) => {
                      const updated = [...selectedVariants];
                      updated[idx].stock = parseInt(e.target.value) || 0;
                      setSelectedVariants(updated);
                    }}
                    className="w-24 border border-gray-300 rounded px-2 py-1"
                    placeholder="Stock"
                  />

                  <input
                    type="number"
                    min="0"
                    value={v.sizePrice ?? 0}
                    onChange={(e) => {
                      const updated = [...selectedVariants];
                      updated[idx].sizePrice = parseInt(e.target.value) || 0;
                      setSelectedVariants(updated);
                    }}
                    className="w-28 border border-gray-300 rounded px-2 py-1"
                    placeholder="Price"
                  />
                </div>
              ))}
            </div>
          )}

        <ModalInput
          label="Colors (comma separated)"
          name="colors"
          value={formData?.colors?.join(", ") ?? ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              colors: e.target.value.split(",").map((c) => c.trim()),
            })
          }
        />

        <ModalInput
          label="Tags (comma separated)"
          name="tags"
          value={formData?.tags?.join(", ") ?? ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              tags: e.target.value.split(",").map((t) => t.trim()),
            })
          }
        />
      </div>
    </CustomModalContainer>
  );
};

export default EditProductModal;
